import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, Rocket, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

import { formatTextWithHashtags } from '../../utils/formatText';
import { formatNumber } from '../../utils/formatNumber';
import { formatDate } from '../../utils/formatDate';
import { useComments } from '../../hooks/useComments';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useAuth } from '../../context/AuthContext';
import PostActions from './PostActions';
import CommentSection from './CommentSection';
import GiftDialog from '../profile/dialogs/GiftDialog';
import LikesPreview from './LikesPreview';

import type { ExtendedPost, MediaItem, SongItem, PopularComment } from '../../types/index';
import PostHeader from './PostHeader';
import CommentSlider from './CommentSlider';
import MediaViewer from './MediaViewer';
import PostView from '../cards/PostView';
import YouTubeEmbed from './YouTubeEmbed';
import LinkPreviewCard from './LinkPreviewCard';
import RepostCard from './RepostCard';

interface PostCardProps {
  post: ExtendedPost;
  mediaItems?: MediaItem[];
  songItems?: SongItem[];
  boosted?: boolean;
  videoOrientation?: 'portrait' | 'landscape';
  onOptionsClick?: () => void;
}

// Generate stable random values outside component to prevent re-renders
const generateStableRandomValue = (postId: string, min: number, max: number): number => {
  let hash = 0;
  for (let i = 0; i < postId.length; i++) {
    const char = postId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % (max - min) + min;
};

function PostCard({ post: initialPost, mediaItems: propMediaItems, songItems: propSongItems, boosted = false, videoOrientation, onOptionsClick }: PostCardProps) {
  
  // Add state for PostView and dialogs
  const [isPostViewOpen, setIsPostViewOpen] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [showGiftDialog, setShowGiftDialog] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);

  // Add post data with defaults
  const post = useMemo(() => ({
    ...initialPost,
    caption: initialPost.caption || '',
    media_url: initialPost.media_url || '',
    media_type: initialPost.media_type || 'image',
    likes: initialPost.likes || 0,
    views: initialPost.views || 0,
    comment_count: initialPost.comment_count || 0,
    bookmarkCount: initialPost.bookmarkCount || 0, // Use real bookmark count from service
    created_at: initialPost.created_at || new Date().toISOString(),
    users: initialPost.users || {
      username: 'Unknown User',
      avatar_url: '',
      verified: false
    }
  }), [initialPost]);

  // Determine post type from media_type or type property
  const postType = post.type || post.media_type || 'image';
  const isImagePost = postType === 'image';
  const isVideoPost = postType === 'video';
  const isShopPost = postType === 'shop';
  const isPollPost = postType === 'poll';
  const isMultiMediaPost = postType === 'multi-media';
  const isMusicPost = postType === 'music' || postType === 'multi-song';

  // Prepare media items from props or post data
  const mediaItems: MediaItem[] = useMemo(() => {
    // First priority: explicitly passed mediaItems prop
    if (propMediaItems && propMediaItems.length > 0) {
      return propMediaItems;
    }
    
    // Second priority: mediaItems array from post data
    if (initialPost.mediaItems && initialPost.mediaItems.length > 0) {
      return initialPost.mediaItems;
    }
    
    // Third priority: create from media_url if available
    if (post.media_url) {
      return [{
        id: `media-${post.id}`,
        url: post.media_url,
        type: post.media_type || 'image',
        thumbnail: post.media_url
      }];
    }
    
    // Fallback: empty array if no media
    return [];
  }, [propMediaItems, initialPost.mediaItems, post.id, post.media_url, post.media_type]);

  const songItems: SongItem[] = propSongItems || initialPost.songItems || [];

  const { user } = useAuth();
  const { submitComment } = useComments(post.id);
  const { bookmarkPost, unbookmarkPost, bookmarkedPosts } = useBookmarks();

  // Check if post is bookmarked
  const isBookmarkedFromHook = useMemo(() => {
    return bookmarkedPosts.some(bookmark => bookmark.post_id === post.id);
  }, [bookmarkedPosts, post.id]);

  // Load initial data
  useEffect(() => {
    setIsBookmarked(isBookmarkedFromHook);
  }, [isBookmarkedFromHook]);

  // Enhanced bookmark toggle with activity tracking
  const handleBookmarkToggle = useCallback(async () => {
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await unbookmarkPost(post.id);
        setBookmarkCount(prev => Math.max(0, prev - 1));
      } else {
        await bookmarkPost(post.id);
        setBookmarkCount(prev => prev + 1);
      }
      setIsBookmarked(!isBookmarked);
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
    setBookmarkLoading(false);
  }, [isBookmarked, bookmarkPost, unbookmarkPost, post.id]);

  const processedComments = useMemo(() => {
    if (!post.comments || !Array.isArray(post.comments)) {
      return [];
    }
    return post.comments.slice(0, 3);
  }, [post.comments]);

  const previewComments: PopularComment[] = useMemo(() => {
    return processedComments.map((comment, index) => ({
      id: comment.id || `comment-${index}`,
      content_id: comment.id || `content-${index}`,
      post_id: post.id,
      user_id: comment.user_id || `user-${index}`,
      content: comment.content || '',
      is_ad: false,
      impressions: 0,
      created_at: comment.created_at || new Date().toISOString(),
      updated_at: comment.updated_at || new Date().toISOString(),
      user: {
        id: comment.user_id || `user-${index}`,
        username: comment.user?.username || 'Anonymous',
        full_name: comment.user?.username || 'Anonymous User',
        avatar_url: comment.user?.avatar_url || '',
        verified: comment.user?.verified || false,
        followers_count: 0,
        is_page: false
      },
      username: comment.user?.username || 'Anonymous',
      avatar_url: comment.user?.avatar_url || '',
      verified: comment.user?.verified || false,
      is_pinned: false,
      type: 'comment' as const,
      likes_count: comment.likes_count || 0,
      replies_count: 0,
      likes: comment.likes_count || 0,
    }));
  }, [processedComments, post.id]);

  const handleMediaView = useCallback(async (mediaId: string) => {
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'media_view', {
          event_category: 'engagement',
          event_label: `post_${post.id}_media_${mediaId}`,
          value: 1
        });
      }
      // TODO: Add API call to update media view count
      // await updateMediaViewCount(post.id, mediaId);
    } catch (error) {
      console.error('Error tracking media view:', error);
    }
  }, [post.id, post.media_type]);

  const handleCommentSubmit = useCallback(async (content: string) => {
    try {
      await submitComment(content);
      setShowCommentInput(false);
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  }, [post.id]);

  const openPostView = useCallback(() => {
    setIsPostViewOpen(true);
  }, [post.id, mediaItems, post.comment_count]);

  const onPostViewClose = useCallback(() => {
    setIsPostViewOpen(false);
  }, [post.id]);

  const toggleCommentInput = useCallback(() => {
    setShowCommentInput(!showCommentInput);
  }, [post.id, showCommentInput]);

  const handleRepost = useCallback((content: string) => {
    console.log('Repost with content:', content);
    // TODO: Implement repost functionality
  }, []);

  const handleShareClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Share clicked for post:', post.id);
  }, [post.id]);

  const handleOriginalPostClick = useCallback(() => {
    // Navigate to original post if this is a repost
    console.log('Navigate to original post');
  }, [post.id]);

  const handleGiftClick = useCallback(() => {
    setShowGiftDialog(true);
  }, []);

  return (
    <>
      <div
        className={`bg-background text-foreground rounded-lg shadow-md overflow-hidden w-full max-w-2xl mx-auto my-4 flex flex-col relative ${isVideoPost ? 'video-post' : ''} ${(boosted || post.is_boosted) ? 'ring-2 ring-[#ffc11f]/30 shadow-lg' : ''}`}
        data-post-type={postType === 'video' ? 'video' : 'image'}
      >
        {/* Post Header */}
        <div className="flex justify-between items-center px-3 py-2">
          <PostHeader
            userId={post.users.id}
            username={post.users.username || 'Unknown User'}
            avatarUrl={post.users.avatar_url || ''}
            verified={post.users.verified || false}
            createdAt={post.created_at || ""}
            location={post.location}
            onOptionsClick={onOptionsClick}
          />
        </div>

        {/* Post Content */}
        {typeof post.caption === 'string' && post.caption.trim() !== '' && (
          <div className="px-3 py-2 flex-shrink-0">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {formatTextWithHashtags(post.caption)}
            </p>
          </div>
        )}

        {/* YouTube Embed */}
        {post.youtubeData && (
          <div className="px-3 py-2">
            <YouTubeEmbed 
              youtubeData={post.youtubeData}
              onPlay={() => console.log('YouTube video played')}
            />
          </div>
        )}

        {/* Link Previews */}
        {post.linkPreviews && post.linkPreviews.length > 0 && (
          <div className="px-3 py-2 space-y-2">
            {post.linkPreviews.map((linkPreview) => (
              <LinkPreviewCard 
                key={linkPreview.id}
                linkPreview={linkPreview}
                onClick={() => console.log('Link preview clicked:', linkPreview.url)}
              />
            ))}
          </div>
        )}

        {/* Repost Card */}
        {post.repostData && (
          <div className="px-3 py-2">
            <RepostCard 
              repostData={post.repostData}
              originalPost={post} // You might want to fetch the actual original post
              onOriginalPostClick={handleOriginalPostClick}
              onRepost={() => handleRepost('simple')}
              onShare={handleShareClick}
            />
          </div>
        )}

        {/* Media Display */}
        <div className={`relative flex-grow w-full ${videoOrientation === 'portrait' ? 'aspect-[9/16]' : videoOrientation === 'landscape' ? 'aspect-[16/9]' : 'aspect-[4/5]'} mx-auto max-h-[80vh] !mb-0 !pb-0`}>
          {((isImagePost || isVideoPost || isShopPost || isMultiMediaPost) || (mediaItems.length > 0)) && (
            <MediaViewer
              media_url={mediaItems.length > 0 ? mediaItems[0].url : ''}
              caption={post.caption || ''}
              mediaType={postType as 'image' | 'video' | 'music'}
              className="w-full h-full"
              onMediaView={handleMediaView}
              additionalMediaUrls={mediaItems.slice(1).map(item => item?.url || '').filter(Boolean)}
              musicInfo={post.music_info}
              postId={post.id}
              postOwnerId={post.user_id}
            />
          )}
          {isMusicPost && songItems && songItems.length > 0 && (
            <MediaViewer
              media_url={songItems[0]?.url || ''}
              caption={songItems[0]?.title || ''}
              mediaType="music"
              className="w-full h-full"
              onMediaView={handleMediaView}
              additionalMediaUrls={songItems.slice(1).map(song => song?.url || '').filter(Boolean)}
              musicInfo={{
                title: (songItems[0] && songItems[0].title) || 'Unknown Track',
                artist: (songItems[0] && songItems[0].artist) || 'Unknown Artist',
                coverArt: (songItems[0] && songItems[0].cover) || '',
                duration: typeof songItems[0]?.duration === 'string' ? parseInt(songItems[0].duration) : (songItems[0]?.duration || 180)
              }}
              postId={post.id}
              postOwnerId={post.user_id}
            />
          )}
        </div>

        {/* Post Meta (views, time, arrow) */}
        <div className="flex items-center justify-between px-4 mt-4 mb-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <span>{formatNumber(post.views)} views</span>
          </div>
          <div className="flex-1 flex justify-center">
            {boosted || post.is_boosted ? (
              <div className="flex items-baseline gap-1 text-xs text-[#ffc11f] font-medium">
                <Rocket className="h-3 w-3" />
                <span>Boosted</span>
              </div>
            ) : (
              <span>{post.created_at ? formatDate(post.created_at) : 'Just now'}</span>
            )}
          </div>
          <div className="flex items-center gap-2 cursor-pointer hover:text-[#065900]" onClick={openPostView}>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>

        {/* Post Actions (like, comment, share, gift, bookmark) */}
        <div className="border-t border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between px-4 mb-2 space-x-2">
            <PostActions
              postId={post.id}
              initialLikeCount={post.likes || 0}
              onCommentClick={toggleCommentInput}
              onRepost={handleRepost}
              commentCount={post.comment_count || 0}
              repostCount={post.reshare_count || 0}
              postTitle={post.caption || `Post by ${post.users.username}`}
              postThumbnail={post.media_url}
              onGiftClick={handleGiftClick}
              onBookmarkClick={handleBookmarkToggle}
              isBookmarked={isBookmarked}
              bookmarkCount={bookmarkCount}
              bookmarkLoading={bookmarkLoading}
            />
          </div>
        </div>

        {/* Likes Preview */}
        {post.likes > 0 && (
          <div className="px-3 py-1">
            <LikesPreview postId={post.id} likeCount={post.likes || 0} />
          </div>
        )}

        {/* Popular Comment Slider */}
        {Array.isArray(previewComments) && previewComments.length > 0 && (
          <div className="px-4 pb-2 mt-2">
            <CommentSlider comments={previewComments} />
          </div>
        )}

        {/* Poll content */}
        {isPollPost && post.pollOptions && post.pollOptions.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
            <h4 className="text-sm font-medium mb-2">Poll</h4>
            <div className="space-y-2">
              {post.pollOptions?.map((option, index) => {
                const totalVotes = post.pollOptions?.reduce((sum, opt) => sum + (opt.votes || 0), 0) || 0;
                const percentage = totalVotes > 0 ? Math.round((option.votes || 0) / totalVotes * 100) : generateStableRandomValue(`${post.id}-${index}`, 10, 90);
                return (
                  <div key={option.id || index} className="flex items-center">
                    <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-8 overflow-hidden relative">
                      <div className="bg-[#065900]/20 h-full transition-all duration-300" style={{ width: `${percentage}%` }} />
                      <div className="absolute inset-0 flex items-center px-3">
                        <span className="text-sm">{option.text}</span>
                      </div>
                      <span className="ml-2 text-sm font-medium">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {post.pollOptions?.reduce((sum, opt) => sum + (opt.votes || 0), 0) || generateStableRandomValue(post.id, 100, 1000)} votes
            </p>
          </div>
        )}
      </div>

      {/* PostView Modal */}
      <PostView
        post={{
          ...post,
          caption: post.caption || undefined,
          users: post.users,
          likes_count: post.likes || 0,
          comments_count: post.comment_count || 0,
          views_count: post.views || 0,
          bookmarks_count: 0,
          gifts_count: 0,
          comments: []
        }}
        isOpen={isPostViewOpen}
        onClose={onPostViewClose}
      />

      {/* Gift Dialog */}
      <GiftDialog
        isOpen={showGiftDialog}
        onClose={() => setShowGiftDialog(false)}
        username={post.users.username}
      />

      {/* Comments Dialog */}
      <Dialog open={showCommentInput} onOpenChange={() => setShowCommentInput(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Comments</DialogTitle>
          </DialogHeader>
          
          {/* Comments List - Scrollable */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-1">
            <CommentSection
              comments={processedComments}
              onCommentSubmit={handleCommentSubmit}
              showInput={false}
              user={user}
            />
          </div>
          
          {/* Comment Input - Fixed at Bottom */}
          <div className="flex-shrink-0 border-t pt-4 mt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 flex-shrink-0">
                <img 
                  src={user?.user_metadata?.avatar_url || "/images/avatars/default.jpg"} 
                  alt={user?.user_metadata?.username || 'user'}
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              
              <div className="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2">
                <input
                  type="text"
                  placeholder="Add a comment..."
                  className="flex-1 bg-transparent border-0 outline-none text-sm placeholder-gray-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                      handleCommentSubmit(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                
                <button 
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
                  onClick={(e) => {
                    const input = e.currentTarget.parentElement?.querySelector('input') as HTMLInputElement;
                    if (input && input.value.trim()) {
                      handleCommentSubmit(input.value);
                      input.value = '';
                    }
                  }}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PostCard;